/**
 * Boundary adapters for employee service assignment.
 *
 * The backend embeds assignments in the employee as
 * `{ service: {id, code, name}, species: [{id, value}] }[]` and takes them back
 * as `{ services: [{ serviceId, speciesIds }] }`. The page only ever sees the
 * id-only form, in one canonical order.
 */

import type { EmployeeServiceEntry } from "@/types/employee";
import type { ServiceSpeciesAssignment } from "@/types/employeeServices";

export type UpdateEmployeeServicesBody = {
  services: ServiceSpeciesAssignment[];
};

/**
 * One entry per service that has species, ids deduplicated, in id order: the
 * same assignment always reads back the same way, however it was sent.
 */
export const normalizeAssignments = (
  assignments: ServiceSpeciesAssignment[],
): ServiceSpeciesAssignment[] => {
  const byService = new Map<number, Set<number>>();
  for (const { serviceId, speciesIds } of assignments) {
    const ids = byService.get(serviceId) ?? new Set<number>();
    for (const id of speciesIds) ids.add(id);
    byService.set(serviceId, ids);
  }

  return [...byService]
    .filter(([, ids]) => ids.size > 0)
    .sort(([a], [b]) => a - b)
    .map(([serviceId, ids]) => ({
      serviceId,
      speciesIds: [...ids].sort((a, b) => a - b),
    }));
};

/** An employee's embedded services, or none when the payload has no list. */
export const adaptEmployeeServices = (
  entries: EmployeeServiceEntry[] | undefined,
): ServiceSpeciesAssignment[] =>
  normalizeAssignments(
    (entries ?? []).map(({ service, species }) => ({
      serviceId: service.id,
      speciesIds: species.map(({ id }) => id),
    })),
  );

export const toUpdateServicesBody = (
  assignments: ServiceSpeciesAssignment[],
): UpdateEmployeeServicesBody => ({
  services: normalizeAssignments(assignments),
});
